// Контракт API пульта (задача D14): «Запуск» — ступень, доля новых чатов, каналы, лимиты, дежурные и остановка.
//
// - `GET /api/v1/launch` (любой вошедший) → `LaunchState`;
// - `POST /api/v1/launch/changes` (admin) `{ change, expectedVersion, confirmed? }` → `{ state, risk, undoId, undoUntil }`.
//   Сервер проверяет по порядку: версия настроек (`version_conflict`), возможности (`not_supported`), повышение без
//   подтверждения (`confirmation_required`), значения (400), затем пишет одну строку журнала `launch.<вид>`. Снижение
//   (`risk: 'safe'`) применяется сразу и отменяется `POST /api/v1/undo` в течение 15 с (`undoId`, `undoUntil`), повышение
//   (`raise`) — только с `confirmed: true`, без отмены;
// - `GET /api/v1/omni/staff?department=QUALIFIER` (admin) → `{ staff }`: кого можно назначить дежурным — активные сотрудники
//   отдела Квалификация.
//
// Пределы лимитов сервер берёт из схемы настроек A9 (`bad_settings` с её текстом): своих D не заводит. Форма лимитов пульта
// (`LaunchBudgetFormSchema`) держит их копию с теми же текстами — совпадение держит `tests/console/launch-routes.test.ts`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { BudgetStateSchema, CHANNEL_MODES, ChannelModeSchema, DaySchema, MomentSchema, STAGE_ORDER, StageSchema } from './common.js';

// ───────────────────────────── состояние ─────────────────────────────

/** Сотрудник Омнидеска коротко: «Андрей П.»; `online` — в Омнидеске статус «в сети» (`ONLINE`). */
export const StaffRefSchema = z.object({ id: z.string().min(1), nameRu: z.string(), online: z.boolean() });
export type StaffRef = z.infer<typeof StaffRefSchema>;

/**
 * Канал в «Запуске»: ключ (Ruling 21) и подпись, кабинеты Авито из диалогов за 7 дней, режим канала из настроек, диалоги
 * с активностью сегодня по МСК и момент последнего черновика Анны в канале (`null` — черновиков не было).
 */
export const LaunchChannelSchema = z.object({
  key: z.string(),
  labelRu: z.string(),
  cabinetsRu: z.array(z.string()),
  mode: ChannelModeSchema,
  todayDialogs: z.number().int().nonnegative(),
  lastDraftAt: MomentSchema.nullable(),
});
export type LaunchChannel = z.infer<typeof LaunchChannelSchema>;

/**
 * Лимиты: расход и лимит за день и месяц по МСК (рубли — до копейки; `month.labelRu` — «Октябрь»), порог оповещения,
 * вызовов модели на диалог, поисков исследователя на диалог (`null` — исследователя ещё нет, C2-2) и состояние бюджета.
 */
export const LaunchBudgetSchema = z.object({
  day: z.object({ date: DaySchema, spentRub: z.number().nonnegative(), limitRub: z.number().nonnegative() }),
  month: z.object({ labelRu: z.string(), spentRub: z.number().nonnegative(), limitRub: z.number().nonnegative() }),
  alertAtPercent: z.number().int(),
  perDialogCalls: z.number().int(),
  researchPerDialog: z.number().int().nullable(),
  state: BudgetStateSchema,
});
export type LaunchBudget = z.infer<typeof LaunchBudgetSchema>;

/**
 * Что пульт умеет включать: «Подсказки» — после правок Омнидеска (план F), бой — отдельным планом (L9). В v1 — оба `false`.
 */
export const LaunchCapabilitiesSchema = z.object({ assist: z.boolean(), live: z.boolean() });
export type LaunchCapabilities = z.infer<typeof LaunchCapabilitiesSchema>;

/**
 * Состояние «Запуска».
 * - `stage` — ступень (`stageOf` A14), `stageSince` — с какого момента она действует (`null` — неизвестно);
 * - `stopped` — Анна остановлена («Остановить везде»): когда, кто (коротко, «Влад Т.»; пустая строка — остановили не из
 *   пульта, имени нет) и на какую ступень её вернёт «Включить снова»; `null` — не остановлена;
 * - `estimatedChatsPerDay` — новые диалоги в среднем за 7 полных дней по МСК до сегодня × доля / 100, округлено; `null` —
 *   новых диалогов за эти дни не было;
 * - `workingHours.days` — номера дней недели как в A9 (`getUTCDay`: 0 — воскресенье … 6 — суббота), время — «ЧЧ:ММ» МСК;
 * - `version` — версия настроек (`settings.updated_at` в мс, настроек ещё не сохраняли — 0): её пульт отдаёт обратно в
 *   `expectedVersion`; `at` — момент ответа.
 */
export const LaunchStateSchema = z.object({
  stage: StageSchema,
  stageSince: MomentSchema.nullable(),
  stopped: z.object({ at: MomentSchema, byName: z.string(), resumeStage: StageSchema }).nullable(),
  sharePercent: z.number().int().min(0).max(100),
  estimatedChatsPerDay: z.number().int().nonnegative().nullable(),
  channels: z.array(LaunchChannelSchema),
  budget: LaunchBudgetSchema,
  workingHours: z.object({ days: z.array(z.number().int().min(0).max(6)), from: z.string(), to: z.string() }),
  duty: z.object({ day: z.array(StaffRefSchema), evening: z.array(StaffRefSchema) }),
  capabilities: LaunchCapabilitiesSchema,
  version: z.number().int().nonnegative(),
  at: MomentSchema,
});
export type LaunchState = z.infer<typeof LaunchStateSchema>;

// ───────────────────────────── изменения ─────────────────────────────

/** Виды изменений «Запуска»; действие журнала — `launch.<вид>`. */
export const LAUNCH_CHANGE_KINDS = ['stage', 'share', 'channel', 'stop_all', 'resume', 'duty', 'budget'] as const;
export type LaunchChangeKind = (typeof LAUNCH_CHANGE_KINDS)[number];

/** Ключ канала — копия `CHANNEL_KEY_PATTERN` A9 (`src/settings/schema.ts`): `avito`, `textback:whatsappb`… */
export const LAUNCH_CHANNEL_KEY_PATTERN = /^[a-z0-9_:.-]{1,64}$/;

/** Тексты отказов в виде тела запроса — по полю: что ждёт сервер. */
export const LAUNCH_BODY_ERRORS_RU = {
  body: 'Тело запроса — JSON вида {"change": {"kind": "share", "to": 10}, "expectedVersion": 1759658400000, "confirmed": true}.',
  kind: `change.kind — вид изменения: ${LAUNCH_CHANGE_KINDS.join(', ')}.`,
  stage: 'to — ступень: shadow, assist, live10, live50 или live100.',
  share: 'to — доля новых чатов: целое число от 0 до 100.',
  channelKey: 'key — ключ канала: строчные латинские буквы, цифры и знаки _ : . -, от 1 до 64 знаков, например textback:whatsappb.',
  channelMode: 'to — режим канала: off, shadow, assist или live.',
  duty: 'day и evening — списки id сотрудников Омнидеска.',
  budget: 'dayRub, monthRub, alertAtPercent и perDialogCalls — числа.',
  expectedVersion: 'expectedVersion — версия настроек из ответа GET /api/v1/launch (поле version).',
  confirmed: 'confirmed — true или false.',
} as const;

const budgetNumber = z.number({ error: LAUNCH_BODY_ERRORS_RU.budget });

/**
 * Изменение «Запуска». Доля — целое 0…100, ключ канала — по шаблону A9; числа лимитов здесь не ограничены: их пределы —
 * схема настроек A9 на сервере (`bad_settings`) и `LaunchBudgetFormSchema` в форме пульта.
 */
export const LaunchChangeSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({ kind: z.literal('stage'), to: z.enum(STAGE_ORDER, { error: LAUNCH_BODY_ERRORS_RU.stage }) }),
    z.object({
      kind: z.literal('share'),
      to: z
        .number({ error: LAUNCH_BODY_ERRORS_RU.share })
        .int(LAUNCH_BODY_ERRORS_RU.share)
        .min(0, LAUNCH_BODY_ERRORS_RU.share)
        .max(100, LAUNCH_BODY_ERRORS_RU.share),
    }),
    z.object({
      kind: z.literal('channel'),
      key: z.string({ error: LAUNCH_BODY_ERRORS_RU.channelKey }).regex(LAUNCH_CHANNEL_KEY_PATTERN, LAUNCH_BODY_ERRORS_RU.channelKey),
      to: z.enum(CHANNEL_MODES, { error: LAUNCH_BODY_ERRORS_RU.channelMode }),
    }),
    z.object({ kind: z.literal('stop_all') }),
    z.object({ kind: z.literal('resume') }),
    z.object({
      kind: z.literal('duty'),
      day: z.array(z.string({ error: LAUNCH_BODY_ERRORS_RU.duty }), { error: LAUNCH_BODY_ERRORS_RU.duty }),
      evening: z.array(z.string({ error: LAUNCH_BODY_ERRORS_RU.duty }), { error: LAUNCH_BODY_ERRORS_RU.duty }),
    }),
    z.object({ kind: z.literal('budget'), dayRub: budgetNumber, monthRub: budgetNumber, alertAtPercent: budgetNumber, perDialogCalls: budgetNumber }),
  ],
  { error: LAUNCH_BODY_ERRORS_RU.kind },
);
export type LaunchChange = z.infer<typeof LaunchChangeSchema>;

/** Тело `POST /api/v1/launch/changes`. */
export const LaunchChangeBodySchema = z.object(
  {
    change: LaunchChangeSchema,
    expectedVersion: z
      .number({ error: LAUNCH_BODY_ERRORS_RU.expectedVersion })
      .int(LAUNCH_BODY_ERRORS_RU.expectedVersion)
      .nonnegative(LAUNCH_BODY_ERRORS_RU.expectedVersion),
    confirmed: z.boolean({ error: LAUNCH_BODY_ERRORS_RU.confirmed }).optional(),
  },
  { error: LAUNCH_BODY_ERRORS_RU.body },
);
export type LaunchChangeBody = z.infer<typeof LaunchChangeBodySchema>;

/** Риск изменения: `raise` — повышение (окно с подтверждением), `safe` — снижение (сразу, с отменой). */
export const LAUNCH_RISKS = ['safe', 'raise'] as const;
export const LaunchRiskSchema = z.enum(LAUNCH_RISKS);
export type LaunchRisk = z.infer<typeof LaunchRiskSchema>;

/**
 * Итог сверки маршрутизации Омнидеска после изменения (L8, `syncOmniRouting`): `ok` — Омнидеск совпадает с «Запуском»;
 * `skipped` — сверять не с чем или сверка выключена; `failed` — Омнидеск не ответил или отказал; `adopted_stop` — Анна
 * переняла остановку Омнидеска (появится со сверкой по таймеру).
 */
export const LAUNCH_OMNI_SYNC_STATUSES = ['ok', 'skipped', 'failed', 'adopted_stop'] as const;

/**
 * Ответ на изменение: новое состояние, риск и отмена (`undoId`, `undoUntil`) — только у снижения, иначе `null`. После
 * изменений, которые трогают маршрутизацию Омнидеска (остановка, включение, ступень, доля, канал), — итог сверки
 * `omniSync` и причина `omniSyncRu` (`null` у `ok`); у дежурных и лимитов этих полей нет.
 */
export const LaunchChangeResponseSchema = z.object({
  state: LaunchStateSchema,
  risk: LaunchRiskSchema,
  undoId: z.string().min(1).nullable(),
  undoUntil: MomentSchema.nullable(),
  omniSync: z.enum(LAUNCH_OMNI_SYNC_STATUSES).optional(),
  omniSyncRu: z.string().nullable().optional(),
});
export type LaunchChangeResponse = z.infer<typeof LaunchChangeResponseSchema>;

/** `GET /api/v1/omni/staff` — активные сотрудники отдела, по фамилии. */
export const OmniStaffResponseSchema = z.object({ staff: z.array(StaffRefSchema) });
export type OmniStaffResponse = z.infer<typeof OmniStaffResponseSchema>;

// ───────────────────────────── форма лимитов ─────────────────────────────

/** Пределы лимитов — копия схемы настроек A9 (раздел `budget`): суммы не меньше 0, порог 1…100 %, вызовов 1…200. */
export const LAUNCH_BUDGET_LIMITS = {
  rubMin: 0,
  alertAtPercent: { min: 1, max: 100 },
  perDialogCalls: { min: 1, max: 200 },
} as const;

/** Тексты замечаний формы лимитов — те же, что отдаёт сервер: замечания A9 (без «указано: …») и правило D14. */
export const LAUNCH_BUDGET_ERRORS_RU = {
  dayRub: 'бюджет на день, ₽ — нужно число не меньше 0',
  monthRub: 'бюджет на месяц, ₽ — нужно число не меньше 0',
  alertAtPercent: 'порог тревоги по бюджету, % от лимита — нужно целое число от 1 до 100',
  perDialogCalls: 'лимит вызовов модели на один диалог — нужно целое число от 1 до 200',
  dayOverMonth: 'Дневной лимит больше месячного',
} as const;

const { rubMin, alertAtPercent, perDialogCalls } = LAUNCH_BUDGET_LIMITS;

/** Форма «Изменить лимиты» пульта (D15): поля и пределы A9, дневной лимит не больше месячного. */
export const LaunchBudgetFormSchema = z
  .object({
    dayRub: z.number({ error: LAUNCH_BUDGET_ERRORS_RU.dayRub }).min(rubMin, LAUNCH_BUDGET_ERRORS_RU.dayRub),
    monthRub: z.number({ error: LAUNCH_BUDGET_ERRORS_RU.monthRub }).min(rubMin, LAUNCH_BUDGET_ERRORS_RU.monthRub),
    alertAtPercent: z
      .number({ error: LAUNCH_BUDGET_ERRORS_RU.alertAtPercent })
      .int(LAUNCH_BUDGET_ERRORS_RU.alertAtPercent)
      .min(alertAtPercent.min, LAUNCH_BUDGET_ERRORS_RU.alertAtPercent)
      .max(alertAtPercent.max, LAUNCH_BUDGET_ERRORS_RU.alertAtPercent),
    perDialogCalls: z
      .number({ error: LAUNCH_BUDGET_ERRORS_RU.perDialogCalls })
      .int(LAUNCH_BUDGET_ERRORS_RU.perDialogCalls)
      .min(perDialogCalls.min, LAUNCH_BUDGET_ERRORS_RU.perDialogCalls)
      .max(perDialogCalls.max, LAUNCH_BUDGET_ERRORS_RU.perDialogCalls),
  })
  .refine((form) => form.dayRub <= form.monthRub, { path: ['dayRub'], error: LAUNCH_BUDGET_ERRORS_RU.dayOverMonth });
export type LaunchBudgetForm = z.infer<typeof LaunchBudgetFormSchema>;

// ───────────────────────────── ошибки ─────────────────────────────

/**
 * Коды ошибок «Запуска» и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`; совпадение держит
 * `tests/console/launch-routes.test.ts`).
 */
export const LAUNCH_ERROR_STATUS = {
  /** Тело не того вида; дежурный не из отдела Квалификация; ступень остановленной не из пульта Анны. */
  bad_request: 400,
  /** Значение вне пределов настроек A9 или дневной лимит больше месячного. */
  bad_settings: 400,
  /** Настройки изменились после того, как пульт их прочитал. */
  version_conflict: 409,
  /** Режим ещё не подключён: «Подсказки» (план F) или бой (план L). */
  not_supported: 409,
  /** Повышение без подтверждения в окне. */
  confirmation_required: 409,
  /** База Омнидеска не ответила (список дежурных). */
  omni_read_failed: 502,
} as const;
export type LaunchErrorCode = keyof typeof LAUNCH_ERROR_STATUS;

/** Постоянные тексты ошибок «Запуска» (копия `API_ERRORS_RU`; дословно из брифа D14). */
export const LAUNCH_FIXED_ERRORS_RU = {
  version_conflict: 'Настройки уже изменились в другом окне — обновите экран',
  confirmation_required: 'Это повышение: подтвердите его в окне',
} as const;

/** Тексты отказов «Запуска» со своим текстом в каждом случае (дословно из брифа D14). */
export const LAUNCH_ERRORS_RU = {
  assistNotSupported: 'Режим «Подсказки» появится после правок Омнидеска (план F)',
  liveNotSupported: 'Боевой режим ещё не подключён: он появится отдельным планом',
  staffNotFound: (id: string): string => `Сотрудник ${id} не найден в отделе Квалификация`,
  dayOverMonth: LAUNCH_BUDGET_ERRORS_RU.dayOverMonth,
  stoppedOutsideConsole: 'Анна остановлена не из пульта: сначала включите её снова («Включить снова»), потом меняйте ступень',
  departmentOnlyQualifier: 'department — только QUALIFIER: дежурных выбирают из отдела Квалификация.',
} as const;
